import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Plus, Trash2, Star, MoreHorizontal, Play, Pencil, Search } from "lucide-react";
import { cn } from "@/lib/cn";

interface ReportRow {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  builtIn?: boolean;
  isShared?: boolean;
  isFavorite?: boolean;
  lastAccessedAt?: string | null;
  user?: { id: string; name: string };
}

const ALL_CATEGORIES_KEY = "__all__";
const FAVORITES_KEY = "__favorites__";

export default function ReportsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState<string>(ALL_CATEGORIES_KEY);
  const [openRowMenu, setOpenRowMenu] = useState<string | null>(null);

  const canned = useQuery({
    queryKey: ["/reports/custom/canned"],
    queryFn: async () => (await api.get("/reports/custom/canned")).data.data as ReportRow[],
  });
  const custom = useQuery({
    queryKey: ["/reports/custom"],
    queryFn: async () => (await api.get("/reports/custom")).data.data as ReportRow[],
  });

  const all = useMemo(
    () => [...(canned.data ?? []), ...(custom.data ?? [])],
    [canned.data, custom.data],
  );

  const categories = useMemo(() => {
    const set = new Set<string>();
    all.forEach((r) => { if (r.category) set.add(r.category); });
    return [...set].sort();
  }, [all]);

  const visible = useMemo(() => {
    let rows = all;
    if (folder === FAVORITES_KEY) rows = rows.filter((r) => r.isFavorite);
    else if (folder !== ALL_CATEGORIES_KEY) rows = rows.filter((r) => (r.category ?? "") === folder);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q),
      );
    }
    // Favorites first, then most-recently accessed, then by name
    return [...rows].sort((a, b) => {
      if (!!a.isFavorite !== !!b.isFavorite) return a.isFavorite ? -1 : 1;
      const la = a.lastAccessedAt ? new Date(a.lastAccessedAt).getTime() : 0;
      const lb = b.lastAccessedAt ? new Date(b.lastAccessedAt).getTime() : 0;
      if (la !== lb) return lb - la;
      return a.name.localeCompare(b.name);
    });
  }, [all, folder, search]);

  const favToggle = useMutation({
    mutationFn: async (r: ReportRow) => {
      await api.post("/reports/custom/state", {
        reportKey: r.id,
        isCustom: !r.builtIn,
        isFavorite: !r.isFavorite,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/reports/custom/canned"] });
      qc.invalidateQueries({ queryKey: ["/reports/custom"] });
    },
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/reports/custom/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/reports/custom"] }),
  });

  return (
    <>
      <PageHeader
        title="Reports"
        description="Pre-built reports and your saved custom views."
        actions={<Button asChild><Link to="/reports/new"><Plus className="mr-1 h-4 w-4"/>Create Report</Link></Button>}
      />

      <div className="p-6">
        <Card>
          {/* Toolbar: folder dropdown + search */}
          <div className="flex flex-wrap items-center gap-3 border-b bg-muted/20 px-4 py-2">
            <Select
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              className="h-9 w-56 bg-background font-medium"
            >
              <option value={ALL_CATEGORIES_KEY}>All Reports ({all.length})</option>
              <option value={FAVORITES_KEY}>★ Favorites ({all.filter((r) => r.isFavorite).length})</option>
              <optgroup label="By category">
                {categories.map((c) => {
                  const n = all.filter((r) => (r.category ?? "") === c).length;
                  return <option key={c} value={c}>{c} Reports ({n})</option>;
                })}
              </optgroup>
            </Select>

            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search Reports"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-72 pl-8"
              />
            </div>
          </div>

          <CardContent className="p-0">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-10"><input type="checkbox" disabled /></Th>
                  <Th className="w-8"></Th>
                  <Th>Report Name</Th>
                  <Th>Description</Th>
                  <Th className="w-32">Last Accessed</Th>
                  <Th className="w-24"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {canned.isLoading && <Tr><Td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!canned.isLoading && visible.length === 0 && (
                  <Tr><Td colSpan={6} className="py-12 text-center text-muted-foreground">
                    {search ? "No reports match your search." : "No reports in this folder yet."}
                  </Td></Tr>
                )}
                {visible.map((r) => (
                  <Tr key={r.id} className="group">
                    <Td><input type="checkbox" disabled /></Td>
                    <Td>
                      <button
                        onClick={() => favToggle.mutate(r)}
                        aria-label={r.isFavorite ? "Unstar" : "Star"}
                        className={cn(
                          "flex h-6 w-6 items-center justify-center rounded transition-colors",
                          r.isFavorite ? "text-amber-500" : "text-muted-foreground hover:text-amber-500",
                        )}
                      >
                        <Star className={cn("h-4 w-4", r.isFavorite && "fill-current")} />
                      </button>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <Link
                          to={r.builtIn ? `/reports/canned/${r.id}` : `/reports/${r.id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {r.name}
                        </Link>
                        {r.builtIn  && <Badge variant="secondary" className="text-[10px]">Built-in</Badge>}
                        {r.isShared && <Badge variant="secondary" className="text-[10px]">Shared</Badge>}
                        <RowMenu
                          open={openRowMenu === r.id}
                          onOpen={() => setOpenRowMenu(r.id)}
                          onClose={() => setOpenRowMenu(null)}
                          report={r}
                          onDelete={() => { if (confirm(`Delete "${r.name}"?`)) delMut.mutate(r.id); }}
                        />
                      </div>
                    </Td>
                    <Td className="text-muted-foreground">{r.description ?? "—"}</Td>
                    <Td className="text-sm text-muted-foreground">
                      {r.lastAccessedAt ? relativeTime(r.lastAccessedAt) : "—"}
                    </Td>
                    <Td>
                      <Button asChild size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100">
                        <Link to={r.builtIn ? `/reports/canned/${r.id}` : `/reports/${r.id}`}>
                          <Play className="mr-1 h-3.5 w-3.5" />Run
                        </Link>
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/** Inline per-row "..." menu. */
function RowMenu({
  open, onOpen, onClose, report, onDelete,
}: {
  open: boolean; onOpen: () => void; onClose: () => void;
  report: ReportRow; onDelete: () => void;
}) {
  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          open ? onClose() : onOpen();
        }}
        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-muted"
        aria-label="Row actions"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={onClose} />
          <div className="absolute left-0 top-full z-20 mt-1 w-40 rounded-md border bg-popover py-1 text-sm shadow-lg">
            <Link
              to={report.builtIn ? `/reports/canned/${report.id}` : `/reports/${report.id}`}
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted"
            >
              <Play className="h-3.5 w-3.5" /> Run
            </Link>
            {!report.builtIn && (
              <Link
                to={`/reports/${report.id}/edit`}
                onClick={onClose}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
            )}
            {!report.builtIn && (
              <button
                onClick={() => { onClose(); onDelete(); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-destructive hover:bg-muted"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)} hrs ago`;
  if (diff < 7 * 86400_000) return `${Math.floor(diff / 86400_000)} days ago`;
  return new Date(iso).toLocaleDateString();
}
